/**
 * Realtime client (IMPLEMENTATION_PLAN §12). Socket events never carry data the
 * UI renders directly (customers get ids only); they tell TanStack Query which
 * keys are stale and the data is refetched over HTTP through the audience filter.
 */
import { io, type Socket } from 'socket.io-client'
import type { Query } from '@tanstack/react-query'

export const SOCKET_EVENTS = ['queue.counts', 'ticket.updated', 'ticket.message', 'sla.warning', 'mention'] as const
export type SocketEvent = (typeof SOCKET_EVENTS)[number]

export interface TicketEventPayload {
  ticketId?: string
  ticketKey?: string
  key?: string
  messageId?: string
  [extra: string]: unknown
}

/** Query-key roots each event makes stale (`ticket*` roots are narrowed to one ticket). */
export function staleRoots(event: SocketEvent): string[] {
  switch (event) {
    case 'queue.counts':
      return ['ticket-counts']
    case 'ticket.updated':
      return ['tickets', 'ticket-counts', 'ticket', 'ticket-audit', 'ticket-attachments']
    case 'ticket.message':
      return ['ticket-messages', 'ticket', 'ticket-attachments', 'ticket-audit', 'tickets']
    case 'sla.warning':
      return ['tickets', 'ticket-counts', 'ticket']
    case 'mention':
      return []
  }
}

/** Roots whose second key element is a ticket id (or key, or number). */
const PER_TICKET = new Set(['ticket', 'ticket-messages', 'ticket-audit', 'ticket-attachments'])

function refersTo(value: unknown, ticketId: string, ticketKey?: string): boolean {
  if (typeof value !== 'string') return false
  if (value === ticketId) return true
  if (!ticketKey) return false
  const num = ticketKey.replace(/^TKT-0*/i, '')
  return value.toUpperCase() === ticketKey.toUpperCase() || value === num
}

/**
 * Should `queryKey` (with cached `data`) be invalidated for an event about
 * `ticketId`? Detail routes are keyed by whatever the URL holds (uuid,
 * `TKT-04812` or `4812`), so the cached detail's own id is checked too.
 */
export function shouldInvalidate(
  event: SocketEvent,
  queryKey: readonly unknown[],
  data: unknown,
  payload: TicketEventPayload | null | undefined,
): boolean {
  const root = queryKey[0]
  if (typeof root !== 'string' || !staleRoots(event).includes(root)) return false
  if (!PER_TICKET.has(root)) return true
  const ticketId = payload?.ticketId
  if (!ticketId) return true
  const ticketKey = payload?.ticketKey ?? payload?.key
  if (refersTo(queryKey[1], ticketId, ticketKey)) return true
  const cached = data as { id?: unknown; ticketId?: unknown } | unknown[] | undefined
  if (Array.isArray(cached)) return cached.some((row) => (row as { ticketId?: unknown })?.ticketId === ticketId)
  return cached?.id === ticketId || cached?.ticketId === ticketId
}

/** Predicate form for `queryClient.invalidateQueries({ predicate })`. */
export function invalidationPredicate(event: SocketEvent, payload: TicketEventPayload | null | undefined) {
  return (query: Query) => shouldInvalidate(event, query.queryKey, query.state.data, payload)
}

/** Origin the socket connects to: the API's origin when VITE_API_URL is absolute, else this page's. */
export function socketOrigin(apiBase: string, pageOrigin: string): string {
  try {
    return new URL(apiBase, pageOrigin).origin
  } catch {
    return pageOrigin
  }
}

export function createSocket(origin: string, token: string): Socket {
  return io(origin, {
    path: '/socket.io',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
  })
}
